// vg-binding: P7.D-27
// vg-binding: P7.D-30
//
// apps/vendor/src/features/inventory/StockTab.tsx
//
// Phase 7 Wave 15 polish — vendor inventory Stock tab.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Pagination } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  useStockList,
  type StockRow,
} from './api.js';
import StockAdjustmentModal from './StockAdjustmentModal.js';

const PAGE_LIMIT = 20;

function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-8)}` : id;
}

interface Props {
  onToast: (msg: string) => void;
}

export function StockTab({ onToast }: Props): JSX.Element {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<StockRow | null>(null);
  const [q, setQ] = useState('');

  const query = useStockList({ limit: PAGE_LIMIT, page, q: q || undefined });
  const rows = query.data?.rows ?? [];

  const isEmpty = !query.isLoading && rows.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <input
          type="search"
          data-testid="inventory-stock-search-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder={t('stock.searchPlaceholder')}
          className="h-9 w-72 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
        />
        {q ? (
          <button
            type="button"
            data-testid="inventory-stock-search-clear"
            onClick={() => {
              setQ('');
              setPage(1);
            }}
            className="text-xs text-pw-text-muted hover:text-pw-text"
          >
            {t('stock.searchClear')}
          </button>
        ) : null}
      </div>

      {query.isLoading && rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-pw-text-muted">{t('stock.loading')}</p>
      ) : null}

      {isEmpty ? (
        <div
          data-testid="inventory-stock-empty"
          className="rounded-xl border border-dashed border-pw-border bg-pw-surface px-6 py-10 text-center"
        >
          <p className="text-sm text-pw-text">{t('stock.emptyTitle')}</p>
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('stock.emptyHint')}
          </p>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-pw-border bg-pw-bg">
          <div className="overflow-x-auto">
          <table
            data-testid="inventory-stock-table"
            className="w-full border-collapse text-sm"
          >
            <thead className="bg-pw-surface text-left text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-4 py-2 w-14">{t('stock.headerImage')}</th>
                <th className="px-4 py-2">{t('stock.headerProduct')}</th>
                <th className="px-4 py-2">{t('stock.headerSku')}</th>
                <th className="px-4 py-2">{t('stock.headerVariant')}</th>
                <th className="px-4 py-2">{t('stock.headerWarehouse')}</th>
                <th className="px-4 py-2 text-right">{t('stock.headerOnHand')}</th>
                <th className="px-4 py-2 text-right">{t('stock.headerAvailable')}</th>
                <th className="px-4 py-2 text-right">{t('stock.headerLowThreshold')}</th>
                <th className="px-4 py-2 text-right">{t('stock.headerActions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.stock_id}
                  data-testid={`inventory-stock-row-${r.stock_id}`}
                  onClick={() => navigate(`/inventory/${r.stock_id}`)}
                  className="cursor-pointer border-t border-pw-border hover:bg-pw-surface"
                >
                  <td className="px-4 py-2">
                    {r.product_image ? (
                      <img
                        src={r.product_image}
                        alt={r.product_name ?? t('stock.imageAlt')}
                        loading="lazy"
                        data-testid={`inventory-stock-image-${r.stock_id}`}
                        className="h-10 w-10 rounded-md border border-pw-border bg-pw-surface object-cover"
                      />
                    ) : (
                      <div
                        data-testid={`inventory-stock-image-placeholder-${r.stock_id}`}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-dashed border-pw-border bg-pw-surface text-[10px] text-pw-text-muted"
                      >
                        {t('stock.imagePlaceholder')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-pw-text">
                    <span className="font-medium">
                      {r.product_name ?? shortId(r.variant_id)}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-pw-text-muted">
                    {r.variant_sku ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">
                    {r.variant_label ?? shortId(r.variant_id)}
                  </td>
                  <td className="px-4 py-2 text-pw-text-muted">
                    {r.warehouse_name ?? shortId(r.warehouse_id)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    <span className={r.is_out_of_stock ? 'text-red-600 font-semibold' : r.is_low_stock ? 'text-yellow-700 font-medium' : 'text-pw-text'}>
                      {r.on_hand}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-pw-text-muted">
                    {r.available}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-pw-text-muted">
                    {r.low_stock_threshold}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      data-testid={`inventory-adjust-btn-${r.stock_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(r);
                      }}
                      className="rounded-md border border-pw-border bg-pw-surface px-3 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
                    >
                      {t('stock.actionAdjust')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : null}

      <Pagination
        currentPage={page}
        pageCount={query.data?.page_count ?? 0}
        totalRows={query.data?.total ?? 0}
        testId="inventory-stock-pagination"
        disabled={query.isFetching}
        onChange={setPage}
      />

      <StockAdjustmentModal
        open={selected !== null}
        stock={selected}
        onClose={() => setSelected(null)}
        onSuccess={(msg) => {
          setSelected(null);
          onToast(msg);
        }}
      />
    </div>
  );
}

export default StockTab;
